import { formatCode, parsePairInput } from '@gymtv/core';
import { claimTv } from '@gymtv/data';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';

/**
 * Opened by scanning the TV's QR code with the phone camera (…/pair?code=ABC234), or typed in.
 * Works signed in only (the App wraps it in sign-in). One tap pairs: never auto-claims, so a
 * stray scan can't link someone else's TV.
 */
export function Pair() {
  const [params] = useSearchParams();
  const { session } = useAuth();
  const fromQr = parsePairInput(params.get('code') ?? '');
  const [code, setCode] = useState(fromQr ? formatCode(fromQr) : '');
  const [name, setName] = useState('Garage TV');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await claimTv(supabase, code, name);
    setBusy(false);
    if (r.ok) setDone(r.device.name);
    else setError(r.error);
  }

  if (done) {
    return (
      <div className="center">
        <div className="card" role="status">
          <div className="brand" style={{ padding: 0 }}>GymTV</div>
          <h1>Paired</h1>
          <p>“{done}” is now linked to your account. It switches to your board within a few seconds.</p>
          <Link className="btn" to="/settings">Manage TVs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <form className="card" onSubmit={submit} aria-label="Pair a TV">
        <div className="brand" style={{ padding: 0 }}>GymTV</div>
        <h1>Pair this TV</h1>
        <p className="muted">Signed in as {session?.user.email}</p>
        {fromQr ? (
          <div className="code-big" aria-label="Code from the TV">{code}</div>
        ) : (
          <label className="field">
            Code shown on the TV
            <input id="pair-code" value={code} onChange={(e) => setCode(e.target.value)} autoCapitalize="characters" autoComplete="off" placeholder="ABC 234" style={{ fontFamily: 'var(--mono)', fontSize: 22, letterSpacing: '0.1em' }} />
          </label>
        )}
        <label className="field">
          TV name
          <input id="tv-name" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary" type="submit" disabled={busy || !code.trim()}>
          {busy ? 'Pairing…' : 'Pair TV'}
        </button>
        <Link to="/programs" className="muted" style={{ textAlign: 'center' }}>Open GymTV on the web instead</Link>
      </form>
    </div>
  );
}
